/**
 * The LOCATION of a calendar event (the .ics file and the Google Calendar link), Alvin's rule (10/08): the venue name plus
 * the full street address when we have it, else the venue name plus the city. Never a bare city, never empty, and never
 * one of our area labels ("South LA", "Venice" on a tech week listing whose venue is only revealed after RSVP).
 * Tested over 30 real listings of every kind: tests/cal_location_test.ts.
 */
export type LocItem = {
  kind?: string | null; title?: string | null; venue_name?: string | null; address?: string | null
  neighborhood?: string | null; city?: string | null
}

const COUNTRY: Record<string, string> = { ca: 'Canada', pt: 'Portugal', gb: 'UK', uk: 'UK', fr: 'France', es: 'Spain', de: 'Germany', jp: 'Japan', mx: 'Mexico', it: 'Italy', nl: 'Netherlands', au: 'Australia' }
const KNOWN: Record<string, string> = { 'los-angeles': 'Los Angeles, CA' }
const title = (w: string) => w.charAt(0).toUpperCase() + w.slice(1)

/** "Los Angeles, CA" for los-angeles; "Vancouver, Canada" for vancouver-ca; "Lisbon, Portugal" for lisbon-pt. */
export function cityName(slug: string | null | undefined): string {
  if (!slug) return KNOWN['los-angeles']
  if (KNOWN[slug]) return KNOWN[slug]
  const parts = slug.split('-')
  const last = parts[parts.length - 1]
  if (parts.length > 1 && last.length === 2) return `${parts.slice(0, -1).map(title).join(' ')}, ${COUNTRY[last] ?? last.toUpperCase()}`
  return parts.map(title).join(' ')
}

const STREET = /\b(st|street|ave|avenue|blvd|boulevard|rd|road|dr|drive|way|pl|place|ln|lane|hwy|highway|pkwy|parkway|ct|court|cir|circle|ter|terrace|sq|square|rua|calle|avenida|travessa|largo|praça)\b\.?/i
/** A street address, not an area or a bare number ("1720, Los Angeles, CA" is a venue called 1720). */
export function isStreetAddress(a: string): boolean {
  const s = a.trim()
  if (/^\d+[a-z]?(?:-\d+)?\s+\S/i.test(s)) return true // "8162 Melrose Avenue, ..."
  return STREET.test(s) && /[,\s]/.test(s) && s.length > 8 // "Moorpark St near Balboa Blvd, Encino, CA"
}

const norm = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()

export function calendarLocation(it: LocItem): string {
  const city = cityName(it.city)
  const addr = (it.address ?? '').trim()
  let venue = (it.venue_name ?? '').trim()
  // a venue that is only an area (tech week: "Venice", "South LA") is not a venue
  if (venue && it.neighborhood && norm(venue) === norm(it.neighborhood)) venue = ''
  if (venue && norm(venue) === norm(city.split(',')[0])) venue = ''
  const name = venue || (it.title ?? '').trim()
  if (addr && isStreetAddress(addr)) {
    const full = /,/.test(addr) ? addr : `${addr}, ${city}`
    return name && !norm(full).startsWith(norm(name)) ? `${name}, ${full}` : full
  }
  return name ? `${name}, ${city}` : city
}
